'use client'

import Link from 'next/link'
import { useTranslation } from '@/i18n/client'
import {
  AwardLegalShell,
  AwardSubArticle,
  AwardPflichtList,
  AwardLaenderGrid,
  type LegalArticle,
} from '@/components/award/legal-shell'

const SEASON = new Date().getFullYear()

const articles: LegalArticle[] = [
  {
    num: '§ 1', numDisplay: '01', title: 'Geltungsbereich', titleI18nKey: 'legal.nutzungTitles.t01',
    body: (
      <>
        <p>
          Diese Allgemeinen Geschäfts- und Nutzungsbedingungen (nachfolgend „Bedingungen")
          gelten für alle Nutzer der Plattform FREE GLIDE LEAGUE, erreichbar unter
          free-glide-league.com (nachfolgend „Plattform").
        </p>
        <p>
          Die Plattform richtet sich international an Modellpiloten, Vereine, Teams und
          sonstige Nutzer, die Flugdaten analysieren, dokumentieren, hochladen, veröffentlichen
          oder an Wertungen teilnehmen möchten. Betreiber im Sinne dieser Bedingungen ist
          Stephan Eich, Modellpilot.EU (Anschrift und Kontakt siehe Impressum).
        </p>
      </>
    ),
  },
  {
    num: '§ 2', numDisplay: '02', title: 'Vertragsgegenstand', titleI18nKey: 'legal.nutzungTitles.t02',
    body: (
      <p>
        Gegenstand des Vertrags ist die Nutzung einer Online-Plattform zur technischen
        Erfassung, Analyse, Visualisierung, Auswertung und Veröffentlichung von Flugdaten,
        Ranglisten, Wertungen, Statistiken, Kartenansichten und zugehörigen Informationen
        im Modellflugsport.
      </p>
    ),
  },
  {
    num: '§ 3', numDisplay: '03', title: 'Rollenabgrenzung', titleI18nKey: 'legal.nutzungTitles.t03',
    body: (
      <>
        <p>
          Der Betreiber organisiert die Plattform, die Wettbewerbsserie, die Regeln, die
          Auswertung, die Klassifizierungen und die Pokalvergabe.
        </p>
        <p>
          Der Betreiber organisiert grundsätzlich nicht den einzelnen dezentralen Flug des
          Nutzers. Jeder Flug wird vom jeweiligen Piloten eigenverantwortlich durchgeführt.
          Der Betreiber übernimmt insbesondere keine Flugleitung, keine behördliche Funktion,
          keine verbindliche Sicherheitsfreigabe und keine verbindliche luftrechtliche
          Prüfung des konkreten Einzelfalls.
        </p>
      </>
    ),
  },
  {
    num: '§ 4', numDisplay: '04', title: 'Vertragsschluss und Vertragssprache', titleI18nKey: 'legal.nutzungTitles.t04',
    body: (
      <>
        <p>
          Der Nutzungsvertrag zwischen Nutzer und Betreiber kommt mit der Bestätigung der
          Verifikations-E-Mail nach der Registrierung zustande. Die reine Nutzung
          öffentlicher Inhalte der Plattform ohne Registrierung begründet keinen
          Nutzungsvertrag, unterliegt jedoch diesen Bedingungen im Umfang der tatsächlichen
          Nutzung.
        </p>
        <p>
          Vertragssprache ist Deutsch. Übersetzungen der Plattform oder dieser Bedingungen
          in andere Sprachen dienen ausschließlich der Information; im Zweifel gilt die
          deutsche Fassung.
        </p>
        <p>
          Der Nutzungsvertrag läuft auf unbestimmte Zeit. Er kann von beiden Seiten jederzeit
          ohne Angabe von Gründen beendet werden.
        </p>
      </>
    ),
  },
  {
    num: '§ 5', numDisplay: '05', title: 'Registrierung und Nutzerkonto', titleI18nKey: 'legal.nutzungTitles.t05',
    body: (
      <>
        <p>
          Soweit einzelne Funktionen eine Registrierung voraussetzen, sind Nutzer
          verpflichtet, bei der Anmeldung vollständige und zutreffende Angaben zu machen.
        </p>
        <p>
          Zugangsdaten sind vertraulich zu behandeln. Der Nutzer ist verpflichtet, unbefugte
          Zugriffe oder Missbrauch seines Nutzerkontos unverzüglich zu melden.
        </p>
        <p>
          Der Betreiber ist berechtigt, Nutzerkonten zu sperren oder zu löschen, wenn falsche
          Angaben gemacht wurden, ein schwerwiegender Verstoß gegen diese Bedingungen vorliegt
          oder der begründete Verdacht einer missbräuchlichen Nutzung besteht.
        </p>
      </>
    ),
  },
  {
    num: '§ 6', numDisplay: '06', title: 'Mindestalter und Minderjährige', titleI18nKey: 'legal.nutzungTitles.t06',
    body: (
      <>
        <p>
          Die Registrierung und Nutzung der registrierungspflichtigen Funktionen ist
          natürlichen Personen ab Vollendung des 16. Lebensjahres gestattet. Diese
          Altersgrenze ergibt sich aus Art. 8 DSGVO in Verbindung mit § 22a BDSG für die
          Einwilligung in die Verarbeitung personenbezogener Daten.
        </p>
        <p>
          Minderjährige zwischen 14 und 16 Jahren dürfen die Plattform nur mit Zustimmung der
          Erziehungsberechtigten nutzen; die Zustimmung ist auf Verlangen nachzuweisen.
          Nutzer unter 14 Jahren sind von der Registrierung ausgeschlossen.
        </p>
      </>
    ),
  },
  {
    num: '§ 7', numDisplay: '07', title: 'Teilnahmevoraussetzungen', titleI18nKey: 'legal.nutzungTitles.t07',
    body: (
      <p>
        Der Nutzer sichert zu, dass er alle für ihn am jeweiligen Flugort geltenden
        rechtlichen, technischen und versicherungsbezogenen Voraussetzungen für den Betrieb
        seines Modells erfüllt.
      </p>
    ),
  },
  {
    num: '§ 8', numDisplay: '08', title: 'Pflichten des Nutzers', titleI18nKey: 'legal.nutzungTitles.t08',
    body: (
      <>
        <p>Der Nutzer ist verpflichtet,</p>
        <AwardPflichtList items={[
          'zutreffende, vollständige und unverfälschte Angaben zu machen,',
          'nur solche Daten und Inhalte hochzuladen, zu deren Nutzung er berechtigt ist,',
          'die Regeln der Plattform, der jeweiligen Wertung und des jeweiligen Flugortes einzuhalten,',
          'keine manipulierten, gefälschten oder bewusst irreführenden Datensätze einzureichen,',
          'keine Inhalte hochzuladen, die Rechte Dritter verletzen oder rechtswidrig sind.',
        ]} />
      </>
    ),
  },
  {
    num: '§ 9', numDisplay: '09', title: 'Internationale Nutzung und Recht am Flugort', titleI18nKey: 'legal.nutzungTitles.t09',
    body: (
      <>
        <p>Die Plattform kann international genutzt werden.</p>
        <p>
          Für die Durchführung eines konkreten Fluges gelten stets zusätzlich und vorrangig
          die zwingenden gesetzlichen, luftrechtlichen, verwaltungsrechtlichen,
          sicherheitsrechtlichen, vereinsrechtlichen, naturschutzrechtlichen,
          grundstücksbezogenen und sonstigen lokalen Vorschriften des Staates und des
          konkreten Aufstiegsortes, an dem der Flug tatsächlich stattfindet.
        </p>
        <p>
          Jeder Nutzer ist selbst verpflichtet, vor dem Flug zu prüfen, welche nationalen,
          regionalen und lokalen Regeln am Flugort gelten, ob Genehmigungen erforderlich sind
          und ob der Betrieb des Modells dort zulässig ist.
        </p>
        <p>
          Unabhängig von den Plattformregeln bleibt für den konkreten Flug immer das
          zwingende Recht am jeweiligen Flugort maßgeblich. In grenzüberschreitenden
          Vertragsverhältnissen innerhalb des Anwendungsbereichs der Rom-I-Verordnung kann
          zwar eine Rechtswahl getroffen werden; zwingender Verbraucherschutz des
          gewöhnlichen Aufenthaltsstaates bleibt aber unberührt.
        </p>
        <AwardLaenderGrid items={[
          { code: 'EU', name: 'Europäische Union', text: 'Durchführungsverordnung (EU) 2019/947, ergänzt durch nationale Vorschriften (z.B. § 21b LuftVO in DE).' },
          { code: 'CH', name: 'Schweiz', text: 'Bundesamt für Zivilluftfahrt (BAZL), folgt weitgehend EU-Verordnung.' },
          { code: 'UK', name: 'Vereinigtes Königreich', text: 'Civil Aviation Authority (CAA), Drone Code Regelungen nach Brexit.' },
          { code: 'US', name: 'USA', text: 'FAA Recreational Rules (Hobby), Part 107 (kommerziell).' },
          { code: 'CA', name: 'Kanada', text: 'Transport Canada, RPAS Safety Assurance Regelwerk.' },
          { code: 'AU', name: 'Australien', text: 'Civil Aviation Safety Authority (CASA).' },
        ]} />
        <p className="award-italic">
          Weitere Länder: jeweils zuständige nationale Luftfahrtbehörde sowie regionale
          und lokale Auflagen.
        </p>
        <p>
          Der Upload einer Flugdatei auf FGL stellt keine Bestätigung, Genehmigung oder
          Zulassung des Fluges durch den Betreiber dar. Die Plattform ist keine
          Flugaufsichtsbehörde und nimmt keine Prüfung der luftrechtlichen Zulässigkeit
          einzelner Flüge vor.
        </p>
      </>
    ),
  },
  {
    num: '§ 10', numDisplay: '10', title: 'Luftrecht, Luftraumstruktur und bereitgestellte Karten', titleI18nKey: 'legal.nutzungTitles.t10',
    body: (
      <>
        <p>
          Der Pilot ist eigenverantwortlich für die Einhaltung der geltenden Vorschriften am
          Aufstiegsort sowie im gesamten genutzten Luftraum verantwortlich. Er ist
          verpflichtet, sich vor jedem Flug eigenständig über die aktuellen Rechte und
          Pflichten zu informieren, gegebenenfalls erforderliche Erlaubnisse für den
          Aufstiegsort einzuholen und die Vereinsregeln am Modellflugplatz oder an sonstigen
          Aufstiegsorten einzuhalten. FREE GLIDE LEAGUE hat darauf keinen Einfluss.
        </p>
        <p>
          Soweit die Plattform 2D- oder 3D-Luftraumdarstellungen, Karten, Geo-Zonen,
          Platzinformationen oder ähnliche Informationen bereitstellt, dienen diese
          ausschließlich der allerersten Orientierung und bilden keine rechtliche Grundlage.
        </p>
        <p>
          Die bereitgestellten Karten basieren auf unterschiedlichen kostenfreien APIs für
          2D- und 3D-Kartendarstellungen. Die dargestellten Luftraumstrukturen stammen von
          openAIP, einer community-gepflegten Datenquelle. openAIP erhebt selbst keinen
          Anspruch auf Vollständigkeit, die Daten werden nicht weltweit flächendeckend
          gepflegt, müssen nicht aktuell sein und können fehlerhaft sein.
        </p>
        <p>
          Auch die bereitgestellte 3D-Analyse zur Beurteilung des Luftraums ist daher nicht
          ausschlaggebend und stellt keine rechtlich verbindliche oder mit Gewährleistung
          versehene Information dar. Sie dient ausschließlich als erste Orientierung.
        </p>
        <p>
          openAIP weist selbst darauf hin, dass seine Daten nicht zertifiziert sind, Fehler
          enthalten können und nicht für Primärnavigation oder Flugplanung verwendet werden
          dürfen. Für Deutschland verweist die DFS darauf, dass geografische Gebiete und
          Kontrollzonen im MapTool der Digitalen Plattform Unbemannte Luftfahrt (DIPUL)
          dargestellt sind und vor jedem Flug zu prüfen sind.
        </p>
      </>
    ),
  },
  {
    num: '§ 11', numDisplay: '11', title: 'Uploads und Nutzungsrechte', titleI18nKey: 'legal.nutzungTitles.t11',
    body: (
      <>
        <p>
          Der Nutzer versichert, dass er an den von ihm hochgeladenen Daten, Dateien, Bildern,
          Texten und sonstigen Inhalten die erforderlichen Rechte besitzt.
        </p>
        <p>
          Der Nutzer räumt dem Betreiber das einfache, nicht ausschließliche, räumlich
          unbeschränkte, zeitlich auf die Dauer des Nutzungsvertrags zuzüglich angemessener
          Archivfristen befristete Recht ein, diese Inhalte zum Betrieb der Plattform, zur
          Analyse, zur Auswertung, zur Regelprüfung, zur Darstellung in Ranglisten und zur
          Dokumentation der Wettbewerbsserie zu speichern, zu verarbeiten und öffentlich
          darzustellen, soweit dies für den Plattformzweck erforderlich ist.
        </p>
        <p>
          <strong>Redaktionelle Verwendung von Foto-Uploads:</strong> Hochgeladene Fotos
          (Galerie, Profilbild, Flug-Bilder) dürfen vom Betreiber zusätzlich verwendet werden
          in News-Beiträgen auf free-glide-league.com, im FGL-Newsletter sowie in der offiziellen
          Facebook-Gruppe der FREE GLIDE LEAGUE. Schöne Modellflugzeug-Fotos können als
          Standard-Modellfoto in der öffentlichen Modell-Datenbank genutzt werden. Bei jeder
          Veröffentlichung wird der Pilot mit Anzeigename und Profil-Link als Bildquelle genannt.
          Das Urheberrecht verbleibt beim Nutzer; eine kommerzielle Drittnutzung außerhalb
          dieser Zwecke findet nicht statt.
        </p>
        <p>
          Beim Hochladen versichert der Nutzer, Urheber des Fotos zu sein oder über sämtliche
          erforderlichen Rechte zu verfügen. Sind weitere Personen auf dem Foto erkennbar,
          garantiert der Nutzer, deren Einwilligung gemäß § 22 KUG eingeholt zu haben, und kann
          diese auf Nachfrage Dritter eigenverantwortlich nachweisen.
        </p>
        <p>
          Die Einwilligung in die redaktionelle Verwendung ist freiwillig und kann jederzeit
          mit Wirkung für die Zukunft per E-Mail an{' '}
          <a href="mailto:info@modellpilot.eu">info@modellpilot.eu</a> widerrufen werden.
          Bereits erfolgte Veröffentlichungen werden nach Widerruf entfernt, soweit dies
          technisch zumutbar ist. Details unter{' '}
          <a href="/datenschutz">§ 14a Datenschutzerklärung</a>.
        </p>
      </>
    ),
  },
  {
    num: '§ 12', numDisplay: '12', title: 'Wertung, Moderation und Korrekturen', titleI18nKey: 'legal.nutzungTitles.t12',
    body: (
      <>
        <p>
          Der Betreiber ist berechtigt, eingereichte Flüge, Klassenzuordnungen,
          Modellangaben und Wertungsdaten zu prüfen, zu korrigieren, umzuklassifizieren,
          vorläufig zu sperren oder endgültig von der Wertung auszuschließen, wenn Zweifel
          an Vollständigkeit, Plausibilität, Datenintegrität oder Regelkonformität bestehen.
        </p>
        <p>
          Der Betreiber darf veröffentlichte Ergebnisse nachträglich korrigieren, wenn
          technische Fehler, Auswertungsfehler, Datenfehler, Regelverstöße oder neue
          Erkenntnisse dies erforderlich machen.
        </p>
      </>
    ),
  },
  {
    num: '§ 13', numDisplay: '13', title: 'Verfügbarkeit', titleI18nKey: 'legal.nutzungTitles.t13',
    body: (
      <p>
        Der Betreiber bemüht sich um eine möglichst unterbrechungsfreie Verfügbarkeit der
        Plattform. Eine jederzeitige Verfügbarkeit, Fehlerfreiheit, Vollständigkeit oder
        dauerhafte Abrufbarkeit wird jedoch nicht geschuldet. Planbare Wartungsarbeiten
        werden, soweit zumutbar, angekündigt.
      </p>
    ),
  },
  {
    num: '§ 14', numDisplay: '14', title: 'Haftung', titleI18nKey: 'legal.nutzungTitles.t14',
    body: (
      <>
        <p>
          Der Betreiber haftet nach den gesetzlichen Vorschriften bei Vorsatz und grober
          Fahrlässigkeit sowie bei Schäden aus der Verletzung des Lebens, des Körpers oder
          der Gesundheit.
        </p>
        <p>
          Bei leicht fahrlässiger Verletzung wesentlicher Vertragspflichten ist die Haftung
          auf den vertragstypischen, vorhersehbaren Schaden begrenzt. Wesentliche
          Vertragspflichten sind solche Pflichten, deren Erfüllung die ordnungsgemäße
          Durchführung des Vertrags überhaupt erst ermöglicht und auf deren Einhaltung der
          Nutzer regelmäßig vertrauen darf.
        </p>
        <p>
          Im Übrigen ist die Haftung des Betreibers für leicht fahrlässig verursachte Schäden
          ausgeschlossen, soweit gesetzlich zulässig.
        </p>
        <p>
          Die vorstehenden Haftungsregelungen gelten auch für gesetzliche Vertreter, Organe,
          Mitarbeiter und Erfüllungsgehilfen des Betreibers. Eine Haftung nach dem
          Produkthaftungsgesetz bleibt unberührt.
        </p>

        <AwardSubArticle num="§ 14a" title="Apps und digitale Hilfsmittel">
          Die vom Betreiber bereitgestellten Apps (Web-Plattform, FGL Coaching App, IGC
          Downloader App) und deren Ausgaben (Sollfahrt-Empfehlungen, Endanflug-Rechner,
          Thermik- und Wetterhinweise, Polaren-Daten, Coaching-Texte, Sprachansagen,
          Wertungen) sind rein analytische Hilfswerkzeuge. Sie ersetzen nicht die
          eigenverantwortliche Kontrolle des Piloten über sein Modell und sein
          Flugverhalten. Die Entscheidung, App-Hinweise umzusetzen oder zu ignorieren,
          trifft allein der Pilot. Die Apps können verzögert, unvollständig oder fehlerhaft
          sein (BLE-Verbindungsabbrüche, GPS-Ungenauigkeit, Sensorik-Abweichungen); sie
          ersetzen keinen Augenkontakt zum Modell, keine eigene Urteilskraft und keine
          Sichtflugregeln.
        </AwardSubArticle>

        <AwardSubArticle num="§ 14b" title="Polaren- und Wetterberechnungen">
          Polaren-Berechnungen, MC-Sollfahrten, Thermik-Prognosen, Endanflug-Rechner und
          alle meteorologisch basierten Empfehlungen sind physikalische Modelle unter
          idealisierten Bedingungen. Sie können die tatsächlichen Verhältnisse vor Ort
          niemals vollständig abbilden. Die App-Empfehlungen sind ausdrücklich als Hinweise
          zu verstehen, die der Pilot stets mit der eigenen Wahrnehmung und den konkreten
          örtlichen Gegebenheiten abgleichen muss. Eine Haftung des Betreibers für Schäden,
          die durch Umsetzung oder Nichtumsetzung solcher Empfehlungen entstehen, ist
          ausgeschlossen, soweit gesetzlich zulässig. § 14 Absätze 1 und 2 bleiben unberührt.
        </AwardSubArticle>

        <AwardSubArticle num="§ 14c" title="Versicherungspflicht und Eigenverantwortung">
          Der Pilot ist verpflichtet, für sein Modell eine gültige Modellflug-Haftpflicht-
          versicherung vorzuhalten, die Personen- und Sachschäden gegenüber Dritten
          abdeckt. Der Abschluss der Versicherung und die laufende Prüfung eines
          ausreichenden Versicherungsschutzes liegen allein beim Piloten. Für Schäden an
          seinem eigenen Modell sowie für Schäden, die durch seine Flugtätigkeit an
          Personen, Sachen oder Modellen Dritter entstehen, ist der Pilot selbst
          verantwortlich.
        </AwardSubArticle>

        <AwardSubArticle num="§ 14e" title="Datensicherung beim Piloten — kein IGC-File-Archiv">
          Die FREE GLIDE LEAGUE ist eine Analyse- und Wertungsplattform, <strong>kein
          IGC-File-Archiv</strong>. Alle IGC-Dateien (Original-Flugaufzeichnungen) müssen
          vom Piloten <strong>selbst für die gesamte Wertungssaison gesichert</strong>
          werden. Der Betreiber sichert die Plattform-Daten regelmäßig (mehrfach täglich
          verschlüsselte Off-Site-Backups), prüft die Backups stichprobenartig und
          betreibt die Infrastruktur mit größter Sorgfalt. Dennoch können in seltenen
          Einzelfällen durch Software-Fehler, Server-Abstürze, Hardware-Defekte oder
          Datenkorruption auch Backups nicht mehr einspielbar sein. Im Fall eines
          solchen Totalverlusts (statistisch sehr selten, aber technisch möglich) übernimmt
          der Betreiber <strong>keine Verantwortung für Datenverluste oder Wertungs­
          nachteile</strong> in der laufenden Saison. Der Pilot wird dringend gebeten,
          die IGC-Originaldateien lokal zu archivieren, damit er im Notfall ohne
          Wertungsverlust neu hochladen kann. § 14 Absätze 1 und 2 (Haftung bei
          Vorsatz, grober Fahrlässigkeit, Verletzung wesentlicher Vertragspflichten)
          bleiben unberührt.
        </AwardSubArticle>
      </>
    ),
  },
  {
    num: '§ 15', numDisplay: '15', title: 'Freistellung', titleI18nKey: 'legal.nutzungTitles.t15',
    body: (
      <p>
        Der Nutzer stellt den Betreiber von Ansprüchen Dritter frei, die auf einer
        rechtswidrigen Nutzung der Plattform, auf unzutreffenden Angaben, auf rechtswidrig
        hochgeladenen Inhalten oder auf Verstößen gegen diese Bedingungen beruhen, soweit
        der Nutzer den Verstoß schuldhaft zu vertreten hat und dies nicht auf einem
        Verschulden des Betreibers beruht.
      </p>
    ),
  },
  {
    num: '§ 16', numDisplay: '16', title: 'Meldeweg für rechtswidrige Inhalte (§ 10 DDG, Art. 16 DSA)', titleI18nKey: 'legal.nutzungTitles.t16',
    body: (
      <>
        <p>
          Nutzer und Dritte können rechtswidrige oder vermutlich rechtswidrige Inhalte dem
          Betreiber jederzeit per E-Mail an{' '}
          <a href="mailto:info@modellpilot.eu">info@modellpilot.eu</a>{' '}
          melden. Die Meldung sollte enthalten:
        </p>
        <AwardPflichtList items={[
          'eine ausreichend konkrete Bezeichnung des beanstandeten Inhalts (URL, ID),',
          'eine nachvollziehbare Begründung, warum der Inhalt rechtswidrig ist,',
          'Namen und Kontaktdaten des Meldenden (soweit nicht ausdrücklich anonym),',
          'eine Erklärung, dass die Angaben nach bestem Wissen wahrheitsgemäß und vollständig sind.',
        ]} />
        <p>
          Der Betreiber prüft eingehende Meldungen unverzüglich und ergreift bei Bekanntwerden
          einer konkreten Rechtsverletzung angemessene Maßnahmen nach §§ 7–10 DDG.
        </p>
      </>
    ),
  },
  {
    num: '§ 17', numDisplay: '17', title: 'Sperrung und Ausschluss', titleI18nKey: 'legal.nutzungTitles.t17',
    body: (
      <p>
        Der Betreiber ist berechtigt, Nutzerkonten zu sperren, Inhalte zu entfernen oder
        Nutzer von einzelnen oder allen Funktionen auszuschließen, wenn ein Verstoß gegen
        diese Bedingungen, gegen geltendes Recht oder gegen die Integrität der Wertung
        vorliegt oder ernsthaft zu befürchten ist.
      </p>
    ),
  },
  {
    num: '§ 18', numDisplay: '18', title: 'Kündigung durch den Nutzer und Löschung', titleI18nKey: 'legal.nutzungTitles.t18',
    body: (
      <>
        <p>
          Der Nutzer kann seinen Nutzungsvertrag jederzeit ohne Angabe von Gründen kündigen.
          Die Kündigung erfolgt durch Löschung des Kontos in den Kontoeinstellungen oder
          formlos per E-Mail an info@modellpilot.eu.
        </p>
        <p>
          Ergänzend kann der Nutzer jederzeit die Löschung personenbezogener Daten nach
          Art. 17 DSGVO verlangen. Einzelheiten regelt die Datenschutzerklärung.
        </p>
      </>
    ),
  },
  {
    num: '§ 19', numDisplay: '19', title: 'Änderungen dieser Bedingungen', titleI18nKey: 'legal.nutzungTitles.t19',
    body: (
      <>
        <p>
          Der Betreiber wird den Nutzer über Änderungen dieser Bedingungen mindestens sechs
          Wochen vor deren geplantem Inkrafttreten per E-Mail an die zuletzt hinterlegte
          Adresse informieren. In der Änderungsmitteilung werden die geänderten Regelungen
          und der Zeitpunkt des Inkrafttretens deutlich hervorgehoben.
        </p>
        <p>
          Widerspricht der Nutzer den Änderungen nicht innerhalb von sechs Wochen nach
          Zugang der Mitteilung, gelten die Änderungen als angenommen. Der Betreiber wird den
          Nutzer in der Änderungsmitteilung ausdrücklich auf die Bedeutung seines Schweigens
          und auf sein Widerspruchsrecht hinweisen.
        </p>
        <p>
          Widerspricht der Nutzer fristgerecht, kann der Betreiber das Vertragsverhältnis zum
          Zeitpunkt des Inkrafttretens der Änderung kündigen. Das Kündigungsrecht des
          Nutzers nach § 18 bleibt unberührt.
        </p>
      </>
    ),
  },
  {
    num: '§ 20', numDisplay: '20', title: 'Datenschutz', titleI18nKey: 'legal.nutzungTitles.t20',
    body: (
      <p>
        Für die Verarbeitung personenbezogener Daten gilt ergänzend die{' '}
        <Link href="/datenschutz">Datenschutzerklärung</Link>.
        Sie ist Bestandteil des Vertragsverhältnisses.
      </p>
    ),
  },
  {
    num: '§ 21', numDisplay: '21', title: 'Kontaktstelle nach Art. 11 DSA', titleI18nKey: 'legal.nutzungTitles.t21',
    body: (
      <p>
        Einheitliche Kontaktstelle für Behörden und Nutzer nach Art. 11 und Art. 12 der
        Verordnung (EU) 2022/2065 (Digital Services Act) ist der Betreiber unter der im
        Impressum genannten Anschrift. Bevorzugtes Kommunikationsmittel ist die E-Mail an{' '}
        <a href="mailto:info@modellpilot.eu">info@modellpilot.eu</a>.
        Kommunikationssprachen sind Deutsch und Englisch.
      </p>
    ),
  },
  {
    num: '§ 22', numDisplay: '22', title: 'Anwendbares Recht', titleI18nKey: 'legal.nutzungTitles.t22',
    body: (
      <>
        <p>
          Auf das Vertragsverhältnis zwischen Betreiber und Nutzer findet deutsches Recht
          unter Ausschluss des UN-Kaufrechts Anwendung.
        </p>
        <p>
          Gegenüber Verbrauchern gilt diese Rechtswahl nur insoweit, als dadurch der Schutz
          durch zwingende Bestimmungen des Rechts des Staates des gewöhnlichen Aufenthalts
          des Verbrauchers nicht entzogen wird.
        </p>
        <p>
          Unabhängig hiervon bleibt für die Durchführung eines konkreten Fluges stets das
          zwingende Recht des jeweiligen Flugortes maßgeblich.
        </p>
      </>
    ),
  },
  {
    num: '§ 23', numDisplay: '23', title: 'Gerichtsstand', titleI18nKey: 'legal.nutzungTitles.t23',
    body: (
      <>
        <p>
          Ist der Nutzer Kaufmann, juristische Person des öffentlichen Rechts oder
          öffentlich-rechtliches Sondervermögen, ist ausschließlicher Gerichtsstand für alle
          Streitigkeiten aus oder im Zusammenhang mit diesem Vertragsverhältnis Berlin.
        </p>
        <p>Zwingende gesetzliche Zuständigkeitsvorschriften bleiben unberührt.</p>
      </>
    ),
  },
  {
    num: '§ 24', numDisplay: '24', title: 'Schlussbestimmungen', titleI18nKey: 'legal.nutzungTitles.t24',
    body: (
      <p>
        Sollten einzelne Bestimmungen dieser Bedingungen ganz oder teilweise unwirksam sein
        oder werden, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt. § 306 BGB
        bleibt unberührt.
      </p>
    ),
  },
  {
    num: '§ 25', numDisplay: '25', title: 'Pilotenbörse (FGL Marketplace)', titleI18nKey: 'legal.nutzungTitles.t25',
    body: (
      <>
        <p>
          <strong>(1) Rolle der Plattform.</strong> Die FGL Pilotenbörse ist ausschließlich ein
          technischer Vermittlungsdienst, mit dem eingeloggte Nutzer Inserate für
          Modellsegelflug-Equipment (Segler, Zubehör, Sensorik, RC-Komponenten, Antrieb)
          veröffentlichen, suchen und tauschen können. Der Betreiber ist <strong>niemals
          Vertragspartei</strong> eines Kauf-, Tausch- oder Schenkungsgeschäfts und übernimmt
          keinerlei Garantie für Identität, Bonität oder Verlässlichkeit der Nutzer.
        </p>
        <p>
          <strong>(2) Kein Zahlungsverkehr über FGL.</strong> Der Betreiber wickelt keine
          Zahlungen ab und stellt keine Treuhand-, Käuferschutz- oder
          Zahlungsabwicklungs-Funktionen bereit. Sämtliche Zahlungen erfolgen direkt zwischen
          den Vertragsparteien auf deren eigenes Risiko.
        </p>
        <p>
          <strong>(3) Privat-zu-Privat.</strong> Die Pilotenbörse richtet sich an Modellpiloten.
          Wer regelmäßig oder mit Gewinnerzielungsabsicht verkauft, gilt als gewerblich und
          muss sich beim Inserieren als gewerblich deklarieren. Gewerbliche Verkäufer sind
          selbst zuständig für ein vollständiges Impressum (§ 5 DDG), eine
          14-Tage-Widerrufsbelehrung sowie 24 Monate gesetzliche Gewährleistung (§§ 437 ff. BGB).
          Eine wahrheitswidrige Selbst-Deklaration als Privat führt zu eigener
          wettbewerbsrechtlicher Verantwortung des Nutzers (UWG).
        </p>
        <p>
          <strong>(4) Pflichtangaben pro Inserat.</strong> Mindestens ein Foto des angebotenen
          Artikels, eine Überschrift, eine wahrheitsgemäße Beschreibung, der Zustand sowie
          eine Preisangabe (Festpreis, Verhandlungsbasis, Tausch oder Auf-Anfrage) sind
          erforderlich. Standortangaben erfolgen nur als verkürzte PLZ-Region zum Schutz der
          Anonymität.
        </p>
        <p>
          <strong>(5) Verbot personenbezogener Kontaktdaten in Inseraten und Nachrichten.</strong>
          {' '}Telefonnummern, externe E-Mail-Adressen, vollständige Postadressen sowie Bank-
          und Zahlungsdaten werden im System automatisch erkannt und gefiltert. Die
          Kontaktaufnahme erfolgt ausschließlich über die plattforminterne Nachrichten-Funktion.
          Diese Filterung dient der Prävention von Phishing, Identitätsdiebstahl und
          unerwünschter Kontaktaufnahme.
        </p>
        <p>
          <strong>(6) Foto-Rechte und Bildinhalte.</strong> Der Nutzer versichert, an allen
          hochgeladenen Fotos die Verwertungsrechte zu besitzen und ausschließlich Bilder der
          tatsächlich angebotenen Ware (keine Hersteller-Werbebilder, keine fremden
          Internet-Bilder) zu verwenden. EXIF-Metadaten (Geo-Tags, Kamera-ID) werden beim
          Upload automatisch entfernt.
        </p>
        <p>
          <strong>(7) Beschwerde- und Löschverfahren.</strong> Jedes Inserat verfügt über einen
          "Melden"-Button. Gemeldete Inhalte werden vom Betreiber zeitnah geprüft und bei
          offensichtlicher Rechtswidrigkeit, Verstoß gegen diese Bedingungen oder Verstoß gegen
          den Liability-Waiver der Pilotenbörse unverzüglich entfernt (§ 10 DDG, Art. 14 DSA).
          Der Betreiber kann Inserate jederzeit ohne Angabe von Gründen entfernen.
        </p>
        <p>
          <strong>(8) Laufzeit der Inserate.</strong> Inserate laufen 30 Tage nach
          Veröffentlichung automatisch ab und können vom Nutzer verlängert oder vorzeitig
          gelöscht werden. Pro Nutzer sind maximal 5 aktive Inserate gleichzeitig möglich.
        </p>
        <p>
          <strong>(9) Kein Betrugsschutz.</strong> Der Betreiber führt keinen Käuferschutz und
          keinen Schiedsspruch durch. Bei Betrug, Nichtlieferung, mangelhafter Ware oder
          Streitigkeiten ist ausschließlich die zivilrechtliche Klärung zwischen den
          Vertragsparteien möglich; ein Schadensersatzanspruch gegen den Betreiber besteht
          nicht.
        </p>
        <p>
          <strong>(10) Liability-Waiver.</strong> Die Nutzung der Pilotenbörse setzt die
          ausdrückliche Annahme eines gesonderten Haftungsausschlusses (Version
          MARKETPLACE_V1) durch den Nutzer voraus. Die Annahme wird mit Datum, Uhrzeit,
          IP-Adresse und User-Agent dokumentiert.
        </p>
      </>
    ),
  },
]

export default function NutzungsbedingungenPage() {
  const { t } = useTranslation()
  return (
    <AwardLegalShell
      eyebrow={t('legal.nutzungsbedingungen.eyebrow')}
      meta={t('legal.nutzungsbedingungen.metaSeason', { season: SEASON })}
      title={t('legal.nutzungsbedingungen.title')}
      gradWord={t('legal.nutzungsbedingungen.gradWord')}
      lead={t('legal.nutzungsbedingungen.lead')}
      chips={[
        { label: t('legal.nutzungsbedingungen.chip1') },
        { label: t('legal.nutzungsbedingungen.chip2') },
        { label: t('legal.nutzungsbedingungen.chip3') },
      ]}
      notice={t('legal.nutzungsbedingungen.notice')}
      tocEyebrow={t('legal.shared.tocEyebrow')}
      tocTitle={t('legal.shared.tocTitle')}
      articles={articles}
      closingEyebrow={t('legal.nutzungsbedingungen.closingEyebrow')}
      closingTitle={
        <>
          {t('legal.nutzungsbedingungen.closingPrefix')}{' '}
          <Link href="/datenschutz" className="award-link">{t('legal.nutzungsbedingungen.closingDatenschutz')}</Link>{' '}
          {t('legal.nutzungsbedingungen.closingAnd')}{' '}
          <Link href="/wettbewerbsordnung" className="award-link">{t('legal.nutzungsbedingungen.closingWettbewerbsordnung')}</Link>.
        </>
      }
    />
  )
}
